/**
 * Admin dashboard with edition-based stats overview
 */

import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  MapPin,
  Building2,
  Landmark,
  Users,
  Home,
  Calendar,
  Database,
  AlertCircle,
  CheckCircle2,
  Upload,
  Layers,
  Target,
  Compass,
  Grid3X3,
  TreeDeciduous,
  Circle,
  Globe,
  Briefcase,
  History,
  RefreshCw,
  FolderSync,
  BellRing,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useStatsOverview, useEditionStats, useReferenceDataStatus, useLoadReferenceData } from '../hooks/queries'
import { cn } from '../../lib/utils'
import { UPDATES_QUERY_KEY, fetchUpdates } from './UpdatesPage'
import type { EditionStats, EditionEntityCounts } from '../services/adminApi'

interface StatCardProps {
  title: string
  value: number | string
  icon: React.ReactNode
  link?: string
  color?: 'primary' | 'secondary' | 'green' | 'purple' | 'red'
}

function StatCard({ title, value, icon, link, color = 'primary' }: StatCardProps) {
  const colorClasses: Record<string, string> = {
    primary: 'bg-primary-subtle text-primary',
    secondary: 'bg-secondary-subtle text-secondary',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    red: 'bg-red-50 text-red-600',
  }

  const content = (
    <div className="bg-gut8-bg-muted rounded-gut8 shadow-gut8-sm border border-border-gut8-light p-6 hover:shadow-gut8-md transition-all hover:-translate-y-0.5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-text-muted">{title}</p>
          <p className="text-2xl font-bold text-gut8-anthracite mt-1">
            {typeof value === 'number' ? value.toLocaleString('de-DE') : value}
          </p>
        </div>
        <div className={cn('p-3 rounded-full', colorClasses[color])}>
          {icon}
        </div>
      </div>
    </div>
  )

  if (link) {
    return <Link to={link}>{content}</Link>
  }

  return content
}

interface EditionHistoryItemProps {
  edition: EditionStats
  isSelected: boolean
  onSelect: () => void
}

function EditionHistoryItem({ edition, isSelected, onSelect }: EditionHistoryItemProps) {
  return (
    <button
      onClick={onSelect}
      className={cn(
        'w-full text-left p-4 rounded-gut8 border transition-all',
        isSelected
          ? 'bg-primary-subtle border-primary ring-2 ring-primary/20'
          : 'bg-gut8-bg-muted border-border-gut8-light hover:border-border-gut8-medium hover:bg-gray-50'
      )}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gut8-anthracite">
              Edition {edition.edition}
            </span>
            {isSelected && (
              <span className="text-xs bg-primary text-text-inverse px-2 py-0.5 rounded">
                Aktiv
              </span>
            )}
          </div>
          <p className="text-sm text-text-muted mt-1">
            Gebietsstand: {edition.reference_date_display}
          </p>
        </div>
        <div className="text-right">
          <p className="text-lg font-semibold text-primary-dark">
            {edition.entity_counts.municipalities.toLocaleString('de-DE')}
          </p>
          <p className="text-xs text-text-muted">Gemeinden</p>
        </div>
      </div>

      {/* Compact stats row */}
      <div className="mt-3 pt-3 border-t border-border-gut8-light flex gap-4 text-xs text-text-muted">
        <span>{edition.entity_counts.states} Länder</span>
        <span>{edition.entity_counts.counties} Kreise</span>
        <span>{edition.entity_counts.municipal_associations.toLocaleString('de-DE')} Verbände</span>
      </div>
    </button>
  )
}

export function Dashboard() {
  const { data: stats, isLoading: statsLoading, error: statsError } = useStatsOverview()
  const { data: editionData, isLoading: editionsLoading, error: editionsError } = useEditionStats()
  const { data: refDataStatus, isLoading: refDataLoading } = useReferenceDataStatus()
  const loadReferenceData = useLoadReferenceData()
  const { data: updateFindings } = useQuery({ queryKey: UPDATES_QUERY_KEY, queryFn: fetchUpdates, staleTime: 0 })
  const openUpdates = updateFindings?.filter((f) => f.status === 'open').length ?? 0

  // Track selected edition
  const [selectedEditionId, setSelectedEditionId] = useState<number | null>(null)

  // Get the currently selected edition or default to the first one with data
  const selectedEdition = useMemo(() => {
    if (!editionData?.editions) return null

    // If no edition selected, use the default (newest with data)
    const targetId = selectedEditionId ?? editionData.selected_edition_id

    return editionData.editions.find(e => e.id === targetId) || editionData.editions[0]
  }, [editionData, selectedEditionId])

  const isLoading = statsLoading || editionsLoading
  const error = statsError || editionsError

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="status-error">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <span>Fehler beim Laden der Statistiken</span>
        </div>
      </div>
    )
  }

  // Use edition counts if available, otherwise fall back to overview counts
  const counts: EditionEntityCounts = selectedEdition?.entity_counts ?? {
    states: stats?.entity_counts?.states ?? 0,
    government_districts: stats?.entity_counts?.government_districts ?? 0,
    counties: stats?.entity_counts?.counties ?? 0,
    municipal_associations: stats?.entity_counts?.municipal_associations ?? 0,
    municipalities: stats?.entity_counts?.municipalities ?? 0,
  }

  const classificationCounts = stats?.classification_counts ?? {
    region_types: 0,
    county_types: 0,
    spatial_planning_regions: 0,
    labor_market_regions: 0,
    travel_regions: 0,
    municipality_type_classifications: 0,
    urban_rural_types: 0,
    centrality_types: 0,
  }

  const editions = editionData?.editions ?? []

  return (
    <div className="space-y-8">
      {/* Header with Edition Indicator */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gut8-anthracite">Dashboard</h1>
          {selectedEdition && (
            <p className="text-text-muted mt-1">
              Gebietsstand: <span className="font-medium text-gut8-anthracite">{selectedEdition.reference_date_display}</span>
              <span className="text-text-muted/70 ml-2">(Edition {selectedEdition.edition})</span>
            </p>
          )}
        </div>
        <Link
          to="/admin/import"
          className="btn-primary"
        >
          <Upload className="w-4 h-4" />
          Import
        </Link>
      </div>

      {/* Database Status */}
      {stats?.database_status === 'empty' && (
        <div className="status-error">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">Datenbank ist leer</span>
          </div>
          <p className="text-sm mt-1">
            Importieren Sie das Gemeindeverzeichnis über den{' '}
            <Link to="/admin/import" className="underline text-secondary hover:text-secondary-light">
              Import-Wizard
            </Link>
            .
          </p>
        </div>
      )}

      {/* Entity Stats */}
      <div>
        <h2 className="text-lg font-semibold text-gut8-anthracite mb-4">Entitäten</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <StatCard
            title="Bundesländer"
            value={counts.states}
            icon={<MapPin className="w-5 h-5" />}
            link="/admin/states"
            color="primary"
          />
          <StatCard
            title="Regierungsbezirke"
            value={counts.government_districts}
            icon={<Building2 className="w-5 h-5" />}
            link="/admin/government-districts"
            color="purple"
          />
          <StatCard
            title="Kreise"
            value={counts.counties}
            icon={<Landmark className="w-5 h-5" />}
            link="/admin/counties"
            color="green"
          />
          <StatCard
            title="Gemeindeverbände"
            value={counts.municipal_associations}
            icon={<Users className="w-5 h-5" />}
            link="/admin/associations"
            color="secondary"
          />
          <StatCard
            title="Gemeinden"
            value={counts.municipalities}
            icon={<Home className="w-5 h-5" />}
            link="/admin/municipalities"
            color="red"
          />
        </div>
      </div>

      {/* Regionalisierung - Regional Classifications */}
      <div>
        <h2 className="text-lg font-semibold text-gut8-anthracite mb-4">Regionalisierung</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Raumordnungsregionen"
            value={classificationCounts.spatial_planning_regions}
            icon={<Globe className="w-5 h-5" />}
            link="/admin/regionalisierung"
            color="green"
          />
          <StatCard
            title="Arbeitsmarktregionen"
            value={classificationCounts.labor_market_regions}
            icon={<Briefcase className="w-5 h-5" />}
            link="/admin/regionalisierung"
            color="secondary"
          />
          <StatCard
            title="Reisegebiete"
            value={classificationCounts.travel_regions}
            icon={<Compass className="w-5 h-5" />}
            link="/admin/regionalisierung"
            color="purple"
          />
        </div>
      </div>

      {/* Typisierung - Type Classifications */}
      <div>
        <h2 className="text-lg font-semibold text-gut8-anthracite mb-4">Typisierung</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <StatCard
            title="Regionstypen"
            value={classificationCounts.region_types}
            icon={<Layers className="w-5 h-5" />}
            link="/admin/typisierung"
            color="primary"
          />
          <StatCard
            title="Kreistypen"
            value={classificationCounts.county_types}
            icon={<Target className="w-5 h-5" />}
            link="/admin/typisierung"
            color="purple"
          />
          <StatCard
            title="Gemeindetypen"
            value={classificationCounts.municipality_type_classifications}
            icon={<Grid3X3 className="w-5 h-5" />}
            link="/admin/typisierung"
            color="red"
          />
          <StatCard
            title="Stadt-Land-Typen"
            value={classificationCounts.urban_rural_types}
            icon={<TreeDeciduous className="w-5 h-5" />}
            link="/admin/typisierung"
            color="green"
          />
          <StatCard
            title="Zentralitätstypen"
            value={classificationCounts.centrality_types}
            icon={<Circle className="w-5 h-5" />}
            link="/admin/typisierung"
            color="secondary"
          />
        </div>
      </div>

      {/* Daten-Updates */}
      <div>
        <h2 className="text-lg font-semibold text-gut8-anthracite mb-4">Daten-Updates</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <StatCard
            title="Offene Daten-Updates"
            value={openUpdates}
            icon={<BellRing className="w-5 h-5" />}
            link="/admin/updates"
            color={openUpdates > 0 ? 'red' : 'green'}
          />
        </div>
      </div>

      {/* Edition History */}
      {editions.length > 0 && (
        <div className="card-gut8">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-5 h-5 text-text-muted" />
            <h2 className="text-lg font-semibold text-gut8-anthracite">Historie</h2>
            <span className="text-sm text-text-muted">
              ({editions.length} Edition{editions.length !== 1 ? 'en' : ''})
            </span>
          </div>

          <p className="text-sm text-text-muted mb-4">
            Klicken Sie auf eine Edition, um die Zahlen oben anzuzeigen.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {editions.map((edition) => (
              <EditionHistoryItem
                key={edition.id}
                edition={edition}
                isSelected={selectedEdition?.id === edition.id}
                onSelect={() => setSelectedEditionId(edition.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Last Import Info (from current/latest edition) */}
      {selectedEdition && (
        <div className="card-gut8">
          <h2 className="text-lg font-semibold text-gut8-anthracite mb-4">Import-Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-subtle rounded-gut8">
                <Database className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-text-muted">Edition</p>
                <p className="font-medium text-gut8-anthracite">{selectedEdition.edition}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-subtle rounded-gut8">
                <Calendar className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-text-muted">Gebietsstand</p>
                <p className="font-medium text-gut8-anthracite">{selectedEdition.reference_date_display}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-status-success/20 rounded-gut8">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-text-muted">Import-Datum</p>
                <p className="font-medium text-gut8-anthracite">
                  {selectedEdition.import_date
                    ? new Date(selectedEdition.import_date).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : '-'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reference Data Management */}
      <div className="card-gut8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FolderSync className="w-5 h-5 text-text-muted" />
            <h2 className="text-lg font-semibold text-gut8-anthracite">Referenzdaten</h2>
          </div>
          <button
            onClick={() => loadReferenceData.mutate(undefined)}
            disabled={loadReferenceData.isPending || refDataLoading}
            className={cn(
              'btn-secondary flex items-center gap-2',
              loadReferenceData.isPending && 'opacity-50 cursor-not-allowed'
            )}
          >
            <RefreshCw className={cn('w-4 h-4', loadReferenceData.isPending && 'animate-spin')} />
            {loadReferenceData.isPending ? 'Lädt...' : 'Neu laden'}
          </button>
        </div>

        <p className="text-sm text-text-muted mb-4">
          Regionalisierung und Typisierung aus CSV-Dateien laden. Diese Daten werden bei leerem Stand automatisch geladen.
        </p>

        {loadReferenceData.isSuccess && (
          <div className="status-success mb-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              <span>Referenzdaten erfolgreich geladen!</span>
            </div>
          </div>
        )}

        {loadReferenceData.isError && (
          <div className="status-error mb-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <span>Fehler beim Laden: {loadReferenceData.error?.message}</span>
            </div>
          </div>
        )}

        {refDataStatus && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 bg-gray-50 rounded-gut8">
              <p className="text-xs text-text-muted">Tabellen gesamt</p>
              <p className="text-xl font-semibold text-gut8-anthracite">{refDataStatus.tables.length}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-gut8">
              <p className="text-xs text-text-muted">Mit Daten</p>
              <p className="text-xl font-semibold text-green-600">
                {refDataStatus.tables.filter(t => !t.is_empty).length}
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-gut8">
              <p className="text-xs text-text-muted">Leere Tabellen</p>
              <p className={cn(
                'text-xl font-semibold',
                refDataStatus.empty_tables.length > 0 ? 'text-red-600' : 'text-green-600'
              )}>
                {refDataStatus.empty_tables.length}
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-gut8">
              <p className="text-xs text-text-muted">CSV-Dateien</p>
              <p className={cn(
                'text-xl font-semibold',
                refDataStatus.csv_files_available ? 'text-green-600' : 'text-red-600'
              )}>
                {refDataStatus.csv_files_available ? 'Vorhanden' : 'Fehlen'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
